import React, { useEffect, useState } from "react"
import { Card, CardBody, Col, Row } from "reactstrap"
import { FORM_NAMES } from "constants/index"
import { connect } from "react-redux"
import {
  arrayPush,
  change,
  Field,
  FieldArray,
  getFormValues,
  initialize,
  reduxForm,
} from "redux-form"

import {
  getConnectedAccountsConfig,
  getConnectedAccountsList,
  getVendorPaymentConfig,
  resetConnectedAccountsConfig,
  resetConnectedAccountsList,
  savePayouts,
} from "store/actions"
import Select from "react-select"
import { FloatingLabelForm } from "components/RenderInput"
import { SaveButton } from "components/Buttons"
import {
  calculateAmounts,
  getDefaultPaymentConfigs,
  getTotalFees,
  getTotalPercentage,
  mapPayoutsAccountsToApi,
  removeAccount,
  renderClientAccounts,
  selectAccount,
  setInitialAccounts,
  setInitialStripeAccounts,
} from "./utils"
import validate from "./payoutsValidation"
import ModalComponent from "components/Modals"
import { userIsGodMode } from "utils"

const Payouts = ({
  entity_id,
  entity_type,
  getConnectedAccountsConfig,
  connectedAccountsConfig,
  menu_item_id,
  getConnectedAccountsList,
  resetConnectedAccountsConfig,
  resetConnectedAccountsList,
  getVendorPaymentConfig,
  vendorId,
  vendorPaymentConfig,
  price,
  connectedAccountsList,
  addAccount,
  change,
  handleSubmit,
  savePayouts,
  formValues,
}) => {
  const [mainAccount, setMainAccount] = useState(null)
  const [mpAccount, setMpAccount] = useState(null)
  const [payouts, setPayouts] = useState([])
  const [stripeAccounts, setStripeAccounts] = useState([])
  const [accountsAreInitialized, setAccountsAreInitialized] = useState(false)
  const [showInvalidPercentageModal, setShowInvalidPercentageModal] =
    useState(false)
  const [showInvalidFeesModal, setShowInvalidFeesModal] = useState(false)
  const [totalPercentage, setTotalPercentage] = useState(0)
  const [totalFees, setTotalFees] = useState(0)
  const [totalAmount, setTotalAmount] = useState(0)

  useEffect(() => {
    getConnectedAccountsList()
    getVendorPaymentConfig({ id: vendorId })
    return () => {
      resetConnectedAccountsConfig()
      resetConnectedAccountsList()
    }
  }, [])

  useEffect(() => {
    if (price) {
      change("price", price)
    }
  }, [price])

  useEffect(() => {
    if (entity_type && entity_id) {
      getConnectedAccountsConfig({
        entity_id,
        entity_type,
        ...(menu_item_id ? { menu_item_id } : {}),
      })
    }
  }, [entity_id, entity_type, menu_item_id])

  useEffect(() => {
    if (connectedAccountsConfig) {
      const initialAccounts = setInitialAccounts(connectedAccountsConfig)
      setMainAccount(initialAccounts?.mainAcc)
      setMpAccount(initialAccounts?.mpAcc)
      setPayouts(initialAccounts?.accounts)
      change("clientAccounts", initialAccounts?.accounts)
      setAccountsAreInitialized(true)
    }
  }, [connectedAccountsConfig])

  useEffect(() => {
    if (connectedAccountsList && accountsAreInitialized) {
      const initValues = setInitialStripeAccounts(
        connectedAccountsList,
        payouts
      )

      if (!mainAccount) {
        setMainAccount({
          percentage: 100,
          main_account: 1,
          account_id: initValues?.mainAcc?.account_id,
          account_name: initValues?.mainAcc?.name,
          stripe_connected_acc_id: initValues?.mainAcc?.id,
          entity_id: entity_id,
          entity_type: entity_type,
          menu_item_id: menu_item_id,
        })
      }
      setStripeAccounts(initValues?.accounts)
    }
  }, [connectedAccountsList, accountsAreInitialized])

  useEffect(() => {
    let initialMpAccValues = { percentage: 0, fee: 0 }
    if (vendorPaymentConfig && !mpAccount) {
      initialMpAccValues = getDefaultPaymentConfigs(
        vendorPaymentConfig,
        entity_type
      )
    }
    change("mainAccPercentage", mainAccount?.percentage)
    change("mainAccFee", mainAccount?.fee)
    change(
      "percentage",
      mpAccount?.percentage || initialMpAccValues?.percentage
    )
    change("fee", mpAccount?.fee || initialMpAccValues?.fee)
  }, [mainAccount, mpAccount, vendorPaymentConfig])

  useEffect(() => {
    if (formValues && accountsAreInitialized) {
      const calculatedAmounts = calculateAmounts(formValues)

      setTotalAmount(calculatedAmounts?.total)
      change("clientAccounts", calculatedAmounts?.clientAccountsWithAmounts)
      change("mainAccAmount", calculatedAmounts?.mainAccAmount || 0)
    }
  }, [formValues])

  const handleSave = data => {
    const totalPercentage = getTotalPercentage(
      data?.mainAccPercentage || 0,
      data?.clientAccounts
    )
    setTotalPercentage(totalPercentage)
    const totalFees = getTotalFees(data?.mainAccFee || 0, data?.clientAccounts)
    setTotalFees(totalFees)
    if (totalPercentage !== 100) {
      setShowInvalidPercentageModal(true)
    } else if (parseFloat(totalFees) > parseFloat(totalAmount)) {
      setShowInvalidFeesModal(true)
    } else {
      const values = {
        vendor_id: parseInt(vendorId),
        entity_id: entity_id,
        entity_type: entity_type,
        menu_item_id:
          entity_type === "form_builder" ? null : menu_item_id || null,
        stripe_accounts: mapPayoutsAccountsToApi(data, mainAccount),
      }
      savePayouts(values)
    }
  }

  return (
    <React.Fragment>
      {!!price && (
        <Row className="mb-3">
          <Col>
            <span style={{ fontSize: "14px" }}>
              <i>NOTE: The price is ${price}</i>
            </span>
          </Col>
        </Row>
      )}
      {userIsGodMode() && (
        <>
          <Row>
            <Col xs="auto">
              <h5>enCappture Payouts:</h5>
            </Col>
          </Row>
          <Row>
            <Col
              xs={5}
              sm={4}
              md={5}
              lg={4}
              xl={2}
              style={{ minWidth: "fit-content" }}
            >
              <div>
                <Field
                  name="percentage"
                  component={FloatingLabelForm}
                  placeholder="Percentage(%)"
                  label="Percentage(%)"
                  type="number"
                  min="0"
                  max="100"
                />
              </div>
            </Col>
            <Col
              xs={5}
              sm={4}
              md={5}
              lg={4}
              xl={2}
              style={{ minWidth: "fit-content" }}
            >
              <Field
                name="fee"
                component={FloatingLabelForm}
                placeholder="Fee($)"
                label="Fee($)"
                type="number"
                step="0.1"
                min="0"
              />
            </Col>
          </Row>
        </>
      )}
      <Row className="my-3">
        <Col>
          <span>
            Configure the splits between all of your connected accounts. The
            splits must be equal to 100%. Fixed fees are removed before the
            percentage split.
            <br />
            Note: The splits are based on the balance after Platform fees and
            Stripe fees are removed.
          </span>
        </Col>
      </Row>
      <Row className="my-3">
        <Col>
          <h6>
            After enCappture & Stripe fees, the total amount to be distributed:
            {" $" + totalAmount}
          </h6>
        </Col>
      </Row>
      <Row>
        <Col xs="auto">
          <h5>Main Account:</h5>
        </Col>
      </Row>
      <Row>
        <Col xs="auto" xl={4} className="align-self-center">
          {mainAccount && mainAccount?.account_name && (
            <span>{mainAccount?.account_name} </span>
          )}
          {mainAccount && mainAccount?.account_id && (
            <>
              (<b>ID:</b> <span> {mainAccount?.account_id}</span>)
            </>
          )}
        </Col>
        <Col
          xs={5}
          sm={4}
          md={5}
          lg={4}
          xl={2}
          style={{ minWidth: "fit-content" }}
        >
          <Field
            name="mainAccPercentage"
            component={FloatingLabelForm}
            placeholder="Percentage(%)"
            label="Percentage(%)"
            type="number"
            min="0"
            max="100"
          />
        </Col>
        <Col
          xs={5}
          sm={4}
          md={5}
          lg={4}
          xl={2}
          style={{ minWidth: "fit-content" }}
        >
          <Field
            name="mainAccFee"
            component={FloatingLabelForm}
            placeholder="Fee($)"
            label="Fee($)"
            type="number"
            step="0.1"
            min="0"
          />
        </Col>
        {!!formValues?.mainAccAmount && (
          <Col xs="auto" xl={1} className="align-self-center">
            ${formValues?.mainAccAmount}
          </Col>
        )}
      </Row>
      <Row className="mt-3">
        <Col xs="auto" className="align-self-center">
          <h5>Client Accounts:</h5>
        </Col>
        {stripeAccounts && stripeAccounts?.length > 0 && (
          <Col xl={4}>
            <Select
              onChange={selected => {
                const newValues = selectAccount(selected, stripeAccounts)
                setPayouts([...payouts, newValues?.selected])
                addAccount(newValues?.selected)
                setStripeAccounts(newValues?.accounts)
              }}
              value={null}
              classNamePrefix="select2-selection"
              options={stripeAccounts?.map(acc => ({
                ...acc,
                value: acc?.id,
                label: (
                  <>
                    <b>Name:</b> {acc?.name} <b>ID:</b> {acc?.account_id}
                  </>
                ),
              }))}
              placeholder="Select Account"
            />
          </Col>
        )}
      </Row>
      <FieldArray
        name="clientAccounts"
        component={renderClientAccounts}
        removeAccount={value => removeAccount(value, payouts)}
        updatePayouts={values => setPayouts(values)}
        updateStripeAccounts={values => setStripeAccounts(values)}
        stripeAccounts={stripeAccounts}
      />
      <Row className="mt-2 d-flex justify-content-center">
        <Col xs="auto">
          <SaveButton
            type="button"
            content="Save"
            onClick={handleSubmit(handleSave)}
          />
        </Col>
      </Row>
      <ModalComponent
        open={showInvalidPercentageModal}
        onClose={() => {
          setShowInvalidPercentageModal(false)
        }}
        title="Invalid total percentage"
      >
        <Row>
          <Col>
            The total percentage for the connected accounts must be 100. Your
            total is: {totalPercentage}. Please change the percentages
            accordingly
          </Col>
        </Row>
        <Row className="mt-2 d-flex justify-content-end">
          <Col xs="auto">
            <SaveButton
              content="OK"
              type="button"
              onClick={() => {
                setShowInvalidPercentageModal(false)
              }}
            />
          </Col>
        </Row>
      </ModalComponent>
      <ModalComponent
        open={showInvalidFeesModal}
        onClose={() => {
          setShowInvalidFeesModal(false)
        }}
        title="Invalid total fixed amounts"
      >
        <Row>
          <Col>
            The total fixed amounts for the connected accounts must be not more
            than ${totalAmount}. Your total is: ${totalFees}. Please change
            fixed amounts accordingly.
          </Col>
        </Row>
        <Row className="my-3 d-flex justify-content-end">
          <Col xs="auto">
            <SaveButton
              content="OK"
              type="button"
              onClick={() => {
                setShowInvalidFeesModal(false)
              }}
            />
          </Col>
        </Row>
      </ModalComponent>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  connectedAccountsConfig: state.Payouts.connectedAccountsConfig,
  connectedAccountsList: state.Payouts.connectedAccountsList,
  vendorId: state.Login.vendorId,
  vendorPaymentConfig: state.clients.vendorPaymentConfig,
  formValues: getFormValues(FORM_NAMES.PAYOUTS)(state),
})

const mapDispatchToProps = dispatch => ({
  getConnectedAccountsConfig: values =>
    dispatch(getConnectedAccountsConfig(values)),
  getConnectedAccountsList: values =>
    dispatch(getConnectedAccountsList(values)),
  resetConnectedAccountsList: () => dispatch(resetConnectedAccountsList()),
  resetConnectedAccountsConfig: () => dispatch(resetConnectedAccountsConfig()),
  getVendorPaymentConfig: values => dispatch(getVendorPaymentConfig(values)),
  initializeForm: values => dispatch(initialize(FORM_NAMES.PAYOUTS, values)),
  addAccount: value =>
    dispatch(arrayPush(FORM_NAMES.PAYOUTS, "clientAccounts", value)),
  change: (field, value) =>
    dispatch(change(FORM_NAMES.PAYOUTS, `${field}`, value)),
  savePayouts: values => dispatch(savePayouts(values)),
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(reduxForm({ form: FORM_NAMES.PAYOUTS, validate: validate })(Payouts))
